/**
 * Platform / DevOps landing — /app/landing/devops
 *
 * Adapter health, ingest stats, events firehose, saved queries, replay
 * tools. The DevOps view is operational-truth-of-the-pipeline; failing
 * adapters and dropped events get top billing.
 */
import { useEffect, useState, useCallback } from 'react'
import {
  Activity, Play, History, Database, Search,
} from 'lucide-react'
import { api } from '../lib/api'
import {
  LandingHeader, HeroTile, TwoColumnBody, Panel, EmptyState,
  StickyFooter, ListRow, safeNum,
} from '../components/landing/LandingShell'

interface AdapterRow { name: string; status?: string; last_run_at?: string; last_error?: string; events_24h?: number }
interface SavedQuery { id: string; name?: string; description?: string }

export default function DevopsLandingPage() {
  const [refreshing, setRefreshing] = useState(false)
  const [adapters, setAdapters]     = useState<AdapterRow[]>([])
  const [eventsTotal, setEvents]    = useState<number>(0)
  const [tableCount, setTables]     = useState<number>(0)
  const [savedQueries, setQueries]  = useState<SavedQuery[]>([])
  const [proposals, setProposals]   = useState<number>(0)
  const [replayPRs, setReplayPRs]   = useState<{ id: string; title?: string; status?: string }[]>([])

  const load = useCallback(() => {
    setRefreshing(true)
    const j = (p: Promise<any>) => p.catch(() => ({ data: null }))
    Promise.all([
      j(api.get('/api/cdm/adapters/health')),
      j(api.get('/api/cdm/overview')),
      j(api.get('/api/cdm/queries')),
      j(api.get('/api/cdm/changes/proposals', { params: { state: 'awaiting_approval' } })),
      j(api.get('/api/cdm/replay/recent', { params: { limit: 5 } })),
    ]).then(([h, o, q, c, r]) => {
      setAdapters(h.data?.adapters || h.data?.results || [])
      setEvents(h.data?.events_24h ?? o.data?.summary?.events_24h ?? 0)
      setTables(o.data?.summary?.table_count ?? o.data?.tables?.length ?? 0)
      setQueries(q.data?.queries || q.data?.results || [])
      setProposals(c.data?.proposals?.length ?? c.data?.count ?? 0)
      setReplayPRs(r.data?.diffs || r.data?.results || [])
    }).finally(() => setRefreshing(false))
  }, [])

  useEffect(() => {
    load()
    const id = setInterval(load, 60_000)
    return () => clearInterval(id)
  }, [load])

  const okAdapters = adapters.filter(a => a.status === 'ok' || a.status === 'healthy').length
  const failingAdapters = adapters.filter(a =>
    a.status && !['ok', 'healthy'].includes(a.status)
  )
  const total = adapters.length || 1
  const healthPct = (okAdapters / total) * 100

  return (
    <div className="space-y-4 animate-fade-in pb-16">
      <LandingHeader
        title="DevOps Landing"
        subtitle="adapters · ingest · saved queries · replay"
        persona="devops"
        onRefresh={load}
        refreshing={refreshing}
      />

      {/* Hero — 6 tiles */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <HeroTile label="Adapter health"
          value={adapters.length === 0 ? '—' : `${healthPct.toFixed(0)}%`}
          tone={adapters.length === 0 ? 'muted' : healthPct >= 95 ? 'emerald' : healthPct >= 80 ? 'amber' : 'rose'}
          sub={adapters.length === 0 ? 'no adapters' : `${okAdapters}/${adapters.length} ok`}
          to="/app/cdm/adapters" />
        <HeroTile label="Failing adapters" value={safeNum(failingAdapters.length)}
          tone={failingAdapters.length > 0 ? 'rose' : 'emerald'} to="/app/cdm/adapters" />
        <HeroTile label="Events 24h" value={safeNum(eventsTotal)} tone="cyan"
          to="/app/cdm/replay" />
        <HeroTile label="CDM tables" value={safeNum(tableCount)} tone="blue"
          to="/app/cdm/specs" />
        <HeroTile label="Saved queries" value={safeNum(savedQueries.length)} tone="purple"
          to="/app/cdm/queries" />
        <HeroTile label="Open ChangeProposals" value={safeNum(proposals)}
          tone={proposals > 0 ? 'amber' : 'emerald'} to="/app/cdm/changes" />
      </div>

      <TwoColumnBody
        left={(
          <>
            <Panel title="Failing / stale adapters" viewAllTo="/app/cdm/adapters">
              {failingAdapters.length === 0 ? (
                <EmptyState message={adapters.length === 0
                  ? 'No adapters configured.'
                  : 'All adapters healthy.'}
                  configureTo="/app/connectors-manage" configureLabel="add an adapter" />
              ) : (
                <div className="space-y-1">
                  {failingAdapters.slice(0, 5).map(a => (
                    <ListRow key={a.name}
                      primary={a.name}
                      secondary={a.last_error || 'stale'}
                      right={<span className="text-rose-light">{a.status}</span>} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Recent ingest per source" viewAllTo="/app/cdm/adapters">
              {adapters.length === 0 ? (
                <EmptyState message="No ingest yet."
                  configureTo="/app/connectors-manage" configureLabel="configure a source" />
              ) : (
                <div className="space-y-1">
                  {adapters.slice(0, 5).map(a => (
                    <ListRow key={a.name}
                      primary={a.name}
                      secondary={a.last_run_at}
                      right={a.events_24h !== undefined ? `${a.events_24h.toLocaleString()} events` : ''} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
        right={(
          <>
            <Panel title="Saved-query gallery" viewAllTo="/app/cdm/queries">
              {savedQueries.length === 0 ? (
                <EmptyState message="No saved queries yet."
                  configureTo="/app/cdm/queries" configureLabel="create one" />
              ) : (
                <div className="space-y-1">
                  {savedQueries.slice(0, 5).map(q => (
                    <ListRow key={q.id}
                      to="/app/cdm/queries"
                      primary={q.name || q.id}
                      secondary={q.description} />
                  ))}
                </div>
              )}
            </Panel>

            <Panel title="Recent replay-diff PRs" viewAllTo="/app/cdm/replay">
              {replayPRs.length === 0 ? (
                <EmptyState message="No replay diffs yet."
                  configureTo="/app/cdm/replay" configureLabel="open replay tool" />
              ) : (
                <div className="space-y-1">
                  {replayPRs.slice(0, 5).map(p => (
                    <ListRow key={p.id}
                      primary={p.title || p.id}
                      right={p.status} />
                  ))}
                </div>
              )}
            </Panel>
          </>
        )}
      />

      <StickyFooter actions={[
        { label: 'Adapters page',  icon: Activity, tone: 'primary',   to: '/app/cdm/adapters' },
        { label: 'Run an adapter', icon: Play,     tone: 'secondary', to: '/app/cdm/adapters' },
        { label: 'Replay tool',    icon: History,  tone: 'ghost',     to: '/app/cdm/replay' },
        { label: 'CDM tables',     icon: Database, tone: 'ghost',     to: '/app/cdm/specs' },
      ]} />

      <span className="hidden"><Search /></span>
    </div>
  )
}
